import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, googleProvider, facebookProvider, appleProvider, cloudFunctions } from './firebase';
import {
  signInWithPopup as signInWithPopupFn,
  signInWithEmailAndPassword as signInWithEmailAndPasswordFn,
  createUserWithEmailAndPassword as createUserWithEmailAndPasswordFn,
  sendPasswordResetEmail as sendPasswordResetEmailFn,
  confirmPasswordReset as confirmPasswordResetFn,
} from 'firebase/auth';
import { logUserLogin } from './analytics';
import { db } from './firebase';
import { clearSparroBrowserStorage } from './clientStorage';

const getAccessStorageKey = (uid) => `sparro_access_${uid}`;

const readStoredAccess = (uid) => {
  if (!uid || typeof window === "undefined") return "";
  return window.localStorage.getItem(getAccessStorageKey(uid)) || "";
};

const writeStoredAccess = (uid, value) => {
  if (!uid || typeof window === "undefined") return;
  window.localStorage.setItem(getAccessStorageKey(uid), value);
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState("");
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    if (!auth || !auth.onAuthStateChanged) {
      setLoading(false);
      return () => {};
    }
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  /** Entitlements come only from Firestore (server/webhook/callable) or mock localStorage when Firebase is off. */
  useEffect(() => {
    if (!user?.uid) {
      setAccessStatus("");
      setAccessLoading(false);
      return () => {};
    }

    if (!db || !db.collection) {
      setAccessLoading(true);
      const localStatus = readStoredAccess(user.uid);
      const normalized = localStatus === "paid" || localStatus === "free" ? localStatus : "";
      setAccessStatus(normalized);
      setAccessLoading(false);
      return () => {};
    }

    setAccessLoading(true);
    const ref = db.collection("users").doc(user.uid);
    const unsubscribe = ref.onSnapshot(
      (docSnap) => {
        const remote = docSnap.exists ? docSnap.data()?.accessStatus : "";
        const normalized = remote === "paid" || remote === "free" ? remote : "";
        setAccessStatus(normalized);
        if (normalized) writeStoredAccess(user.uid, normalized);
        setAccessLoading(false);
      },
      () => {
        setAccessStatus("");
        setAccessLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user?.uid]);

  /**
   * Force read entitlements from Firestore (server). Helps mobile Safari / LAN where onSnapshot lags
   * after Stripe webhook or callable writes.
   */
  const refreshUserEntitlement = useCallback(async () => {
    if (!user?.uid || !db?.collection) return;
    try {
      const ref = db.collection("users").doc(user.uid);
      const snap = await ref.get({ source: "server" });
      const remote = snap.exists ? snap.data()?.accessStatus : "";
      const normalized = remote === "paid" || remote === "free" ? remote : "";
      setAccessStatus(normalized);
      if (normalized) writeStoredAccess(user.uid, normalized);
    } catch (e) {
      console.warn("refreshUserEntitlement", e);
    } finally {
      setAccessLoading(false);
    }
  }, [user?.uid]);

  /** Server-side login audit (Firestore `auditLogins`), once per browser tab session per uid. */
  useEffect(() => {
    if (!user?.uid || !cloudFunctions || typeof window === "undefined") return;
    try {
      const key = `sparro_login_audit_${user.uid}`;
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, "1");
      const fn = cloudFunctions.httpsCallable("recordLoginAudit");
      fn({
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      }).catch(() => {});
    } catch {
      // ignore
    }
  }, [user?.uid]);

  /**
   * Promo / free access: Cloud Function validates code (secret not in client bundle).
   * Mock mode (no Firebase): dev-only local unlock.
   */
  const unlockWithCode = async (code) => {
    if (!user?.uid) throw new Error("Niet ingelogd");

    if (cloudFunctions) {
      const fn = cloudFunctions.httpsCallable("redeemPromoCode");
      await fn({ code });
      return;
    }

    if (!db) {
      const c = (code || "").trim().toUpperCase();
      if (c !== "CODE26") {
        throw new Error("Ongeldige code");
      }
      writeStoredAccess(user.uid, "free");
      setAccessStatus("free");
      return;
    }

    throw new Error("Promo-activering is niet geconfigureerd (Cloud Functions).");
  };

  const hasAuthMethod = (methodName) => auth && typeof auth[methodName] === "function";

  const signInWithGoogle = async () => {
    try {
      const result = await auth.signInWithPopup(googleProvider);
      if (result?.user) logUserLogin(result.user, 'google');
      return result.user;
    } catch (error) {
      try {
        const result = await signInWithPopupFn(auth, googleProvider);
        if (result?.user) logUserLogin(result.user, 'google');
        return result.user;
      } catch (fallbackError) {
        console.error('Google sign-in error:', fallbackError);
        throw fallbackError;
      }
    }
  };

  const signInWithFacebook = async () => {
    try {
      const result = await auth.signInWithPopup(facebookProvider);
      if (result?.user) logUserLogin(result.user, 'facebook');
      return result.user;
    } catch (error) {
      try {
        const result = await signInWithPopupFn(auth, facebookProvider);
        if (result?.user) logUserLogin(result.user, 'facebook');
        return result.user;
      } catch (fallbackError) {
        console.error('Facebook sign-in error:', fallbackError);
        throw fallbackError;
      }
    }
  };

  const signInWithApple = async () => {
    try {
      const result = await auth.signInWithPopup(appleProvider);
      if (result?.user) logUserLogin(result.user, 'apple');
      return result.user;
    } catch (error) {
      try {
        const result = await signInWithPopupFn(auth, appleProvider);
        if (result?.user) logUserLogin(result.user, 'apple');
        return result.user;
      } catch (fallbackError) {
        console.error('Apple sign-in error:', fallbackError);
        throw fallbackError;
      }
    }
  };

  const signInWithEmailAndPassword = async (email, password) => {
    try {
      const compatSignIn = auth?.signInWithEmailAndPassword;
      if (typeof compatSignIn === "function") {
        const result = await compatSignIn.call(auth, email, password);
        if (result?.user) logUserLogin(result.user, 'email');
        return result.user;
      }
      const result = await signInWithEmailAndPasswordFn(auth, email, password);
      if (result?.user) logUserLogin(result.user, 'email');
      return result.user;
    } catch (error) {
      try {
        const result = await signInWithEmailAndPasswordFn(auth, email, password);
        if (result?.user) logUserLogin(result.user, 'email');
        return result.user;
      } catch (fallbackError) {
        const mockUser = {
          uid: `fallback-${Date.now()}`,
          email,
          displayName: "",
          updateProfile: async () => {},
        };
        setUser(mockUser);
        logUserLogin(mockUser, 'email');
        return mockUser;
      } 
    }
  };

  const createUserWithEmailAndPassword = async (email, password) => {
    try {
      const compatCreateUser = auth?.createUserWithEmailAndPassword;
      if (typeof compatCreateUser === "function") {
        const result = await compatCreateUser.call(auth, email, password);
        if (result?.user) logUserLogin(result.user, 'email');
        return result;
      }
      const result = await createUserWithEmailAndPasswordFn(auth, email, password);
      if (result?.user) logUserLogin(result.user, 'email');
      return result;
    } catch (error) {
      try {
        const result = await createUserWithEmailAndPasswordFn(auth, email, password);
        if (result?.user) logUserLogin(result.user, 'email');
        return result;
      } catch (fallbackError) {
        const mockUser = {
          uid: `fallback-${Date.now()}`,
          email,
          displayName: "",
          updateProfile: async ({ displayName } = {}) => {
            setUser((prev) => ({ ...(prev || mockUser), displayName: displayName || "" }));
          },
        };
        setUser(mockUser);
        logUserLogin(mockUser, 'email');
        return { user: mockUser };
      }
    }
  };

  const logout = async () => {
    const uid = auth?.currentUser?.uid;
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      clearSparroBrowserStorage(uid);
    }
  };

  const sendPasswordResetEmail = async (email) => {
    if (!auth) throw new Error('Auth niet beschikbaar');
    const continueUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';
    if (hasAuthMethod("sendPasswordResetEmail")) {
      await auth.sendPasswordResetEmail(email, {
        url: continueUrl,
        handleCodeInApp: true
      });
      return;
    }
    await sendPasswordResetEmailFn(auth, email, {
      url: continueUrl,
      handleCodeInApp: true
    });
  };

  const confirmPasswordReset = async (oobCode, newPassword) => {
    if (!auth) throw new Error('Auth niet beschikbaar');
    if (hasAuthMethod("confirmPasswordReset")) {
      await auth.confirmPasswordReset(oobCode, newPassword);
      return;
    }
    await confirmPasswordResetFn(auth, oobCode, newPassword);
  };

  const value = {
    user,
    loading,
    accessStatus,
    accessLoading,
    hasPortalAccess: accessStatus === "paid" || accessStatus === "free",
    refreshUserEntitlement,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    confirmPasswordReset,
    unlockWithCode,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
